"use client";

import React, { useRef, useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";

import { setupMapEventHandlers, addMarkersFromVectorTile } from "./util";
import { loadMarkerIcons } from "./loadMarkerIcons";
import MapLoader from "../components/MapLoader";
import brandConfig from "../config/brandConfig";
import { getMapStyleWithProject, loadMapStyle } from "../config/mapStyleConfig";

import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

// Use centralized config for Mapbox token
export const accessToken = (mapboxgl.accessToken = brandConfig.mapboxToken);

const Map = ({ data, onLoad, onFeatureClick, onParcelClick, onLotClick, projectId }) => {
  const mapContainer = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const onParcelClickRef = useRef(onParcelClick);
  const onLotClickRef = useRef(onLotClick);

  let mapRef = useRef(null);

  // Ref para siempre tener el projectId más reciente (evita closures stale)
  const projectIdRef = useRef(projectId);
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  // Update the ref when the callback changes
  useEffect(() => {
    onParcelClickRef.current = onParcelClick;
  }, [onParcelClick]);

  useEffect(() => {
    onLotClickRef.current = onLotClick;
  }, [onLotClick]);

  // Cuando cambia projectId, actualizar el estilo del mapa
  useEffect(() => {
    if (!mapRef.current || !projectId) return;

    const applyStyle = () => {
      if (!mapRef.current) return;
      const newStyle = getMapStyleWithProject(projectId);
      mapRef.current.setStyle(newStyle);

      // Re-configurar handlers después de cambiar el estilo
      mapRef.current.once('style.load', async () => {
        if (!mapRef.current) return;
        await loadMarkerIcons(mapRef.current);
        setupMapEventHandlers(mapRef.current, onParcelClickRef, onLotClickRef, projectId);
        addMarkersFromVectorTile(mapRef.current, projectId);
      });
    };

    if (mapRef.current.isStyleLoaded()) {
      applyStyle();
    } else {
      // El estilo aún no cargó - esperar a que cargue para aplicar el filtro
      mapRef.current.once('style.load', applyStyle);
    }
  }, [projectId]);

  useEffect(() => {
    // Cargar estilo desde URL remota antes de inicializar el mapa
    loadMapStyle().then(() => {
      // Usar el projectId más reciente del ref (no el valor stale del closure)
      const currentProjectId = projectIdRef.current;

      // GUARD: No inicializar el mapa sin projectId para evitar cargar tiles de todos los proyectos
      if (!currentProjectId) {
        console.warn('[Map] No projectId available, deferring map initialization');
        return;
      }

      const mapStyle = getMapStyleWithProject(currentProjectId);

      const map = (mapRef.current = new mapboxgl.Map({
        style: mapStyle,
        container: mapContainer.current,
        center: [-60, -15],
        bearing: 0,
        pitch: 0,
        maxPitch: 85,
        zoom: 2,
        minZoom: 1,
        maxZoom: 20,
        projection: 'globe',
        hash: true,
        // Performance optimizations
        fadeDuration: 0, // Disable tile fade animation for instant rendering
        trackResize: true,
        refreshExpiredTiles: false, // Don't auto-refresh expired tiles
      }));

      map.on("load", async () => {
        // Cargar iconos desde map-styles para symbol layers
        await loadMarkerIcons(map);

        // Usar el projectId más reciente (pudo haber cambiado durante la carga)
        const latestProjectId = projectIdRef.current;

        // Si el projectId cambió desde que se creó el mapa, actualizar el estilo
        if (latestProjectId && latestProjectId !== currentProjectId) {
          const newStyle = getMapStyleWithProject(latestProjectId);
          map.setStyle(newStyle);
          map.once('style.load', async () => {
            await loadMarkerIcons(map);
            setupMapEventHandlers(map, onParcelClickRef, onLotClickRef, latestProjectId);
            addMarkersFromVectorTile(map, latestProjectId);
          });
        } else {
          setupMapEventHandlers(map, onParcelClickRef, onLotClickRef, latestProjectId);
          addMarkersFromVectorTile(map, latestProjectId);
        }

        onLoad(map);
        setMapLoaded(true);
      });

      // Hide loader when map is fully idle (all tiles loaded)
      map.once("idle", () => {
        setIsLoading(false);
      });
    });

    // Cleanup function to remove the map
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []); // Empty dependency array - only run once

  return (
    <>
      <div ref={mapContainer} className="h-full w-full" />
      <MapLoader isLoading={isLoading} />
    </>
  );
};

export default Map;